import React, { useContext, useEffect, useRef, useState } from "react";
import { SessionContext } from "../../context/SessionContext";
import CinemaIcon from "../cinema/CinemaIcon";
import { CustomChatMessage } from "../customChatMessage";
import { postChatBotApi } from "../../api/chatbot/chatBotApi";
import { getCompassCopy } from "../../statics/compassCopy";
import "./ChatBotComponent.scss";

const ChatbotComponent = () => {
  const copy = getCompassCopy();
  const { activePlayerCount = 0, pictureInPicture = false } = useContext(SessionContext) || {};
  const hiddenForPlayback = activePlayerCount > 0 || pictureInPicture;
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const launcher = useRef(null);
  const input = useRef(null);
  const transcript = useRef(null);
  const request = useRef(null);
  const nextId = useRef(0);
  const mounted = useRef(true);
  const requestTimeout = useRef(null);

  useEffect(() => {
    if (hiddenForPlayback) setIsOpen(false);
  }, [hiddenForPlayback]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(requestTimeout.current);
      if (request.current) request.current.abort();
    };
  }, []);

  useEffect(() => {
    if (isOpen && input.current) input.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (transcript.current)
      transcript.current.scrollTop = messages.length ? transcript.current.scrollHeight : 0;
  }, [messages, pending, isOpen]);

  const close = () => {
    setIsOpen(false);
    if (launcher.current) launcher.current.focus();
  };

  const send = async (text, retryId) => {
    const trimmed = text.trim();
    if (!trimmed || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    const responseId = ++nextId.current;
    if (retryId)
      setMessages((items) => items.filter((item) => item.id !== retryId));
    else {
      setMessages((items) => [
        ...items,
        { id: ++nextId.current, role: "user", text: trimmed }
      ]);
      setMessage("");
    }
    setPending(true);
    requestTimeout.current = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await postChatBotApi(
        trimmed,
        localStorage.getItem("userToken"),
        controller.signal
      );
      if (!mounted.current) return;
      if (typeof response?.message !== "string" || !response.message.trim())
        throw new Error("Compass response has no message field");
      setMessages((items) => [
        ...items,
        { id: responseId, role: "assistant", text: response.message }
      ]);
    } catch (error) {
      if (!mounted.current) return;
      console.warn("Atlas Compass unavailable:", error.name, error.message);
      setMessages((items) => [
        ...items,
        { id: responseId, role: "error", text: copy.error, retryText: trimmed }
      ]);
    } finally {
      clearTimeout(requestTimeout.current);
      request.current = null;
      if (mounted.current) setPending(false);
    }
  };

  if (hiddenForPlayback) return null;

  return (
    <div className="compass">
      <button
        ref={launcher}
        type="button"
        className={`chatbot-button ${isOpen ? "is-open" : ""}`}
        aria-label="Atlas Compass"
        aria-expanded={isOpen}
        aria-controls="atlas-compass-panel"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        <CinemaIcon name={isOpen ? "close" : "compass"} />
        <span className="cinema-compass-label">Compass</span>
      </button>
      {isOpen && (
        <section
          id="atlas-compass-panel"
          className="compass-panel"
          role="dialog"
          aria-label="Atlas Compass"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              close();
            }
          }}
        >
          <header className="compass-header">
            <span className="compass-mark">
              <CinemaIcon name="compass" />
            </span>
            <div>
              <h2>
                Atlas <span>Compass</span>
              </h2>
              <p>{copy.assistant}</p>
            </div>
            <button
              type="button"
              className="compass-close"
              onClick={close}
              aria-label={copy.close}
            >
              <CinemaIcon name="close" />
            </button>
          </header>
          <div className="compass-transcript" ref={transcript}>
            {!messages.length && (
              <div className="compass-welcome">
                <div className="compass-welcome-mark" aria-hidden="true">
                  <CinemaIcon name="sparkle" />
                </div>
                <span className="cinema-eyebrow">{copy.eyebrow}</span>
                <h3>{copy.title}</h3>
                <p>{copy.description}</p>
                <div className="compass-prompts">
                  {copy.prompts.map((prompt) => (
                    <button
                      type="button"
                      key={prompt}
                      onClick={() => {
                        setMessage(prompt);
                        if (input.current) input.current.focus();
                      }}
                    >
                      <span>{prompt}</span>
                      <CinemaIcon name="arrow" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div
              role="log"
              aria-label={copy.conversation}
              aria-live="polite"
              aria-relevant="additions"
            >
              {messages.map((item) => (
                <article
                  key={item.id}
                  className={`compass-message compass-message-${item.role}`}
                >
                  <span className="compass-message-author">
                    {item.role === "user" ? copy.you : "COMPASS"}
                  </span>
                  <div className="compass-message-body">
                    {item.role === "assistant" ? (
                      <CustomChatMessage message={item.text} />
                    ) : (
                      item.text
                    )}
                  </div>
                  {item.role === "error" && (
                    <button
                      type="button"
                      className="compass-retry"
                      disabled={pending}
                      onClick={() => send(item.retryText, item.id)}
                    >
                      {copy.retry}
                      <CinemaIcon name="arrow" />
                    </button>
                  )}
                </article>
              ))}
            </div>
            {pending && (
              <div className="compass-thinking" role="status">
                <span>
                  <i />
                  <i />
                  <i />
                </span>
                {copy.thinking}
              </div>
            )}
          </div>
          <form
            className="compass-composer"
            onSubmit={(event) => {
              event.preventDefault();
              send(message);
            }}
          >
            <label className="compass-sr-only" htmlFor="compass-message">
              {copy.placeholder}
            </label>
            <div className="compass-input-wrap">
              <textarea
                ref={input}
                id="compass-message"
                rows={2}
                maxLength={2000}
                value={message}
                placeholder={copy.placeholder}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    !event.shiftKey &&
                    !event.nativeEvent.isComposing
                  ) {
                    event.preventDefault();
                    send(message);
                  }
                }}
              />
              <button
                type="submit"
                disabled={pending || !message.trim()}
                aria-label={copy.send}
              >
                <CinemaIcon name="send" />
              </button>
            </div>
            <p>
              <CinemaIcon name="sparkle" />
              {copy.note}
            </p>
          </form>
        </section>
      )}
    </div>
  );
};

export default ChatbotComponent;
